'use es6';

import I18n from 'I18n';
import { List, Map as ImmutableMap, Set as ImmutableSet, fromJS } from 'immutable';
import { getFrequencyEndDate, getDailyGoals, padBuckets, rebucket } from './calculations';
import { getQuotas } from '../../data/quotasDao';
import { userInfo as getUserInfo } from 'reporting-data/request/user-info';
import * as Frequency from 'reporting-data/constants/frequency';
import * as ConfigTypes from 'reporting-data/constants/configTypes';
import * as DataTypes from 'reporting-data/constants/dataTypes';
import * as Operators from 'reporting-data/constants/operators';
import * as QuotaTypes from 'reporting-data/constants/quotaTypes';
import { getFilterByProperty } from 'reporting-data/config/filters/functions';
import { Filter } from 'reporting-data/config/filters';
import { hasGoalsAccess, getReportOwners, hasQuotaSeries } from '../../reports/utils/quota';
import makeDateRangeByType from 'reporting-data/lib/makeDateRangeByType';
import { TABLE } from 'reporting-data/constants/chartTypes';
import { getCustomFilters, getFilterGroups, getReportConfig } from 'reporting-data/report/configReportGetters';
const COUNT_PROPERTY = 'count';
const getEngagementFilter = (engagementType, property = 'hs_engagement_type') => Filter({
  operator: Operators.IN,
  property,
  values: [engagementType]
});
const ENGAGEMENT_MEETING_FILTER_LEGACY = getEngagementFilter('MEETING', 'engagement.type');
const ENGAGEMENT_CALL_FILTER_LEGACY = getEngagementFilter('CALL', 'engagement.type');
const ENGAGEMENT_MEETING_FILTER = getEngagementFilter('MEETING');
const ENGAGEMENT_CALL_FILTER = getEngagementFilter('CALL');
const NON_REVENUE_SALES_GOAL_SCOPE = 'non-revenue-sales-goals-access';
const SERVICE_GOAL_SCOPE = 'service-goals-access';
const REVENUE_GOAL_SCOPE = 'revenue-goals-access';
const GOALS_MAPPING = fromJS({
  [QuotaTypes.DEALS_CREATED]: (dataType, metricProperties, scopes) => dataType === DataTypes.DEALS && metricProperties.contains(COUNT_PROPERTY) && scopes.includes(NON_REVENUE_SALES_GOAL_SCOPE),
  [QuotaTypes.REVENUE]: dataType => {
    /* SEE RA-1653
    const properties = Set([
      'amount',
      'projectedAmount',
      'closedAmount',
      'amount_in_home_currency',
      'projectedAmountInHomeCurrency',
      'closedAmountInHomeCurrency',
    ]);
    */

    return dataType === DataTypes.DEALS;
  },
  [QuotaTypes.TICKETS_CLOSED]: (dataType, metricProperties, scopes) => dataType === DataTypes.TICKETS && metricProperties.contains(COUNT_PROPERTY) && scopes.includes(SERVICE_GOAL_SCOPE),
  [QuotaTypes.MEETINGS_BOOKED]: (dataType, metricProperties, scopes, config) => {
    const engagementFilter = Filter(getFilterByProperty(config, 'engagement.type') || getFilterByProperty(config, 'hs_engagement_type'));
    const hasMeetingsFilter = engagementFilter.equals(ENGAGEMENT_MEETING_FILTER) || engagementFilter.equals(ENGAGEMENT_MEETING_FILTER_LEGACY);
    const hasCountMetric = metricProperties.contains(COUNT_PROPERTY);
    return hasCountMetric && (dataType === DataTypes.MEETINGS || dataType === DataTypes.ENGAGEMENT && hasMeetingsFilter) && scopes.includes(NON_REVENUE_SALES_GOAL_SCOPE);
  },
  [QuotaTypes.CALLS_MADE]: (dataType, metricProperties, scopes, config) => {
    const callFilter = Filter(getFilterByProperty(config, 'engagement.type') || getFilterByProperty(config, 'hs_engagement_type'));
    const hasCallsFilter = callFilter.equals(ENGAGEMENT_CALL_FILTER) || callFilter.equals(ENGAGEMENT_CALL_FILTER_LEGACY);
    const hasCountMetric = metricProperties.contains(COUNT_PROPERTY);
    return hasCountMetric && (dataType === DataTypes.CALLS || dataType === DataTypes.ENGAGEMENT && hasCallsFilter) && scopes.includes(NON_REVENUE_SALES_GOAL_SCOPE);
  },
  [QuotaTypes.REVENUE]: (dataType, metricProperties, scopes) => dataType === DataTypes.DEALS && scopes.includes(REVENUE_GOAL_SCOPE)
});
export const getGoalType = (report, scopes) => {
  const config = report.get('config');
  const dataType = config.get('dataType');
  const configType = config.get('configType');
  if (configType !== ConfigTypes.TIME_SERIES) {
    return null;
  }
  const metrics = config.get('metrics').map(metric => metric.get('property')).toSet();
  return GOALS_MAPPING.findKey(isValid => isValid(dataType, metrics, scopes, config));
};
export const addGoalSeriesToReport = ({
  report,
  goalSeries,
  goalType
}) => report.updateIn(['displayParams', 'customSeries'], (customSeries = List()) => customSeries.map(cs => cs === 'datetime.quota' ? ImmutableMap({
  type: 'datetime.goal',
  timeUnit: 'MONTH',
  currency: true,
  name: I18n.text(`customSeries.goals.${goalType}`),
  goalData: goalSeries
}) : cs));
export const getGoalSeries = ({
  report,
  response,
  goalResponse
}) => {
  const buckets = response.getIn([0, 'dimension', 'buckets'], List());
  const frequency = report.getIn(['config', 'frequency'], Frequency.MONTH);
  const dates = buckets.map(bucket => bucket.get('key')).update(datesWithoutEnd => datesWithoutEnd.push(getFrequencyEndDate({
    date: datesWithoutEnd.last(),
    frequency
  })));
  const dateFormat = 'YYYYMMDD';
  const {
    config: {
      dataType,
      filters: {
        dateRange: {
          value: reportRange
        } = {}
      } = {}
    }
  } = report.toJS();
  const {
    startDate: reportStart,
    endDate: reportEnd
  } = makeDateRangeByType(reportRange, dateFormat, dataType);
  const reportStartMoment = I18n.moment(reportStart, dateFormat);
  const reportEndMoment = I18n.moment(reportEnd, dateFormat);
  const startDate = goalResponse.size !== 0 ? goalResponse.first().get('key') : I18n.moment(dates.first()).format('YYYY-MM-01');
  const endDate = goalResponse.size !== 0 ? goalResponse.last().get('key') : I18n.moment(dates.last()).format('YYYY-MM-01');
  const goalsByMonth = goalResponse.map(bucket => ImmutableMap({
    date: bucket.get('key'),
    value: bucket.get('sum')
  }));
  const paddedGoalsByMonth = padBuckets({
    goalsByMonth,
    startDate,
    endDate
  });
  const dailyGoals = getDailyGoals({
    goalsByMonth: paddedGoalsByMonth
  }).filter(goal => {
    const goalMoment = I18n.moment(goal.get('date'), 'YYYY-MM-DD');
    return !goalMoment.isBefore(reportStartMoment) && !goalMoment.isAfter(reportEndMoment);
  });
  return rebucket({
    dailyGoals,
    dates
  });
};
export const getDateRange = (config, reportResponse) => {
  const buckets = reportResponse.getIn([0, 'dimension', 'buckets'], List());
  if (buckets.isEmpty()) {
    return null;
  }
  const firstBucket = buckets.first().get('key');
  const lastBucket = buckets.last().get('key');
  if (!I18n.moment(firstBucket).isValid() || !I18n.moment(lastBucket).isValid()) {
    return null;
  }
  const frequency = config.get('frequency', Frequency.MONTH);
  const startMonth = I18n.moment(firstBucket).month() + 1;
  const startYear = I18n.moment(firstBucket).year();
  const enddate = getFrequencyEndDate({
    date: lastBucket,
    frequency
  });
  const endMonth = I18n.moment(enddate).month() + 1;
  const endYear = I18n.moment(enddate).year();
  return {
    startDate: {
      month: startMonth,
      year: startYear
    },
    endDate: {
      month: endMonth,
      year: endYear
    }
  };
};
const USE_PIPELINE_FILTER = ImmutableSet([DataTypes.DEALS, DataTypes.TICKETS]);
export const getPipelineFilters = config => {
  const dataType = config.get('dataType');
  const pipelineFilter = getFilterByProperty(config, 'pipeline') || getFilterByProperty(config, 'hs_pipeline');
  return USE_PIPELINE_FILTER.contains(dataType) && pipelineFilter ? {
    pipelinesOperator: pipelineFilter.get('operator'),
    pipelines: pipelineFilter.get('values')
  } : null;
};
const getRequestPayload = (report, reportResponse, goalType) => {
  const config = getReportConfig(report);
  const PipelinesFilter = getPipelineFilters(config);
  const dateRange = getDateRange(config, reportResponse);
  return dateRange && goalType ? getReportOwners(report).then(owners => owners ? owners.map(owner => owner.get('ownerId')) : []).then(ownerIds => ownerIds.length !== 0 ? {
    assignees: ownerIds,
    assigneesOperator: Operators.IN
  } : null).then(owners => {
    return Object.assign({
      customFilters: getCustomFilters(config),
      filterGroups: getFilterGroups(config)
    }, PipelinesFilter, owners, {
      dateRange
    });
  }) : null;
};
export const addQuotaToDeals = (report, reportResponse) => {
  if (!hasQuotaSeries(report) || report.get('chartType') === TABLE) {
    return Promise.resolve(report);
  }
  const goalType = getGoalType(report);
  return Promise.all([getRequestPayload(report, reportResponse, goalType), getUserInfo()]).then(([requestPayload, userInfo]) => requestPayload && hasGoalsAccess(userInfo) ? getQuotas(requestPayload, goalType).then(quotas => addGoalSeriesToReport({
    report,
    goalSeries: getGoalSeries({
      report,
      response: reportResponse,
      goalResponse: quotas
    }),
    goalType
  })) : Promise.resolve(report));
};